import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, FolderOpen, FileAudio } from "lucide-react";
import { api } from "@/lib/api";

const CHUNK = 2 * 1024 * 1024;
const AUDIO_RE = /\.(mp3|flac|wav|m4a|ogg|aiff|wma|vob|mp4|mkv|avi|mov|mpg|mpeg|wmv)$/i;
const ACCEPT = ".mp3,.flac,.wav,.m4a,.ogg,.aiff,.wma,.vob,.mp4,.mkv,.avi,.mov,.mpg,.mpeg,.wmv";

async function uploadFile(file, sessionId, onProgress) {
  const init = await api.post("/rip/upload/init", {
    session_id: sessionId, filename: file.name, size: file.size });
  const uploadId = init.data.upload_id;
  for (let off = 0; off < file.size; off += CHUNK) {
    await api.post(`/rip/upload/chunk/${uploadId}`, file.slice(off, off + CHUNK), {
      headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.min(95, Math.round(((off + CHUNK) / file.size) * 100)));
  }
  const done = await api.post(`/rip/upload/complete/${uploadId}`);
  onProgress(100);
  return done.data;
}

async function entryFiles(entry, out) {
  if (entry.isFile) {
    const f = await new Promise((res, rej) => entry.file(res, rej));
    if (AUDIO_RE.test(f.name)) out.push(f);
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    for (;;) {
      const batch = await new Promise((res, rej) => reader.readEntries(res, rej));
      if (!batch.length) break;
      for (const e of batch) await entryFiles(e, out);
    }
  }
}

export const UploadZone = ({ sessionId, onUploaded, onNeedSession }) => {
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});

  const handleFiles = async (fileList, folder = "") => {
    const files = Array.from(fileList || []).filter((f) => AUDIO_RE.test(f.name));
    if (!files.length) {
      toast.error("No supported files found — MP3, FLAC, WAV, M4A, OGG, AIFF, WMA or video (VOB, MP4, MKV, AVI, MOV, MPG)");
      return;
    }
    let sid = sessionId;
    if (!sid && onNeedSession) {
      try {
        sid = await onNeedSession(folder);
      } catch {
        toast.error("Could not create a session for this upload");
        return;
      }
    }
    if (!sid) { toast.error("Create a rip session first"); return; }
    setBusy(true);
    for (const f of files) {
      try {
        setProgress((p) => ({ ...p, [f.name]: 0 }));
        const t = await uploadFile(f, sid, (pct) => setProgress((p) => ({ ...p, [f.name]: pct })));
        if (t.dup_status === "flagged") toast.warning(`${f.name}: possible duplicate — ${t.dup_reason}`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    setBusy(false);
    setProgress({});
    onUploaded(sid);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const entries = Array.from(e.dataTransfer.items || [])
      .map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
      .filter(Boolean);
    const plainFiles = Array.from(e.dataTransfer.files || []);
    if (!entries.some((en) => en.isDirectory)) {
      handleFiles(plainFiles);
      return;
    }
    (async () => {
      const out = [];
      for (const en of entries) await entryFiles(en, out);
      const folder = entries.length === 1 && entries[0].isDirectory ? entries[0].name : "";
      handleFiles(out, folder);
    })();
  };

  const pickFolder = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const folder = files[0]?.webkitRelativePath?.split("/")[0] || "";
    handleFiles(files, folder);
  };

  return (
    <div
      data-testid="rip-upload-zone"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      className="rounded-xl border-2 border-dashed border-line bg-bg-surface px-6 py-8 text-center cursor-pointer hover:border-brand-amber/60 transition"
    >
      <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
        data-testid="rip-file-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden"
        data-testid="rip-folder-input" onChange={pickFolder} />
      {busy ? (
        <div className="space-y-2">
          <Loader2 className="h-6 w-6 animate-spin text-brand-amber mx-auto" />
          {Object.entries(progress).map(([name, pct]) => (
            <div key={name} className="text-xs font-mono text-ink-secondary">{name} — {pct}%</div>
          ))}
        </div>
      ) : (
        <>
          <UploadCloud className="h-7 w-7 text-brand-amber mx-auto mb-2" />
          <div className="text-sm font-semibold">Drop tracks or an album folder here, or click to browse</div>
          <div className="text-xs text-ink-muted mt-1 font-mono">
            MP3 · FLAC · WAV · M4A · OGG · AIFF · WMA (300MB) — plus video discs: VOB · MP4 · MKV · AVI · MOV · MPG (1GB), audio auto-extracted to MP3.
          </div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-ink-secondary hover:text-brand-amber hover:border-brand-amber/60 transition"
              data-testid="rip-browse-files-btn">
              <FileAudio className="h-3 w-3" /> Browse files
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); !busy && folderRef.current?.click(); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-ink-secondary hover:text-brand-amber hover:border-brand-amber/60 transition"
              data-testid="rip-browse-folder-btn">
              <FolderOpen className="h-3 w-3" /> Browse album folder
            </button>
          </div>
        </>
      )}
    </div>
  );
};
